<script setup lang="ts">
import { ref } from 'vue';
import { whenever } from '@vueuse/core';
import { useGlobal, useMap, useApp } from '@/composables';
import type { CarColors } from '@/types';
import { carColors } from '@/types';

const MAX_NUMBER_OF_CARS_PER_ROAD = 5;
const DURATION = 20;
const { isLoadingScreenTransitionendActive } = useGlobal();
const { isHighQuality: isCarHighQuality } = useMap();

const getCarSprite50 = (color: string) => {
  return new URL(`/src/assets/images/Cars/Cars_sprites_50/car-${color}-1.avif`, import.meta.url)
    .href;
};
const getCarSprite100 = (color: string) => {
  return new URL(`/src/assets/images/Cars/Cars_sprites_100/car-${color}-1.avif`, import.meta.url)
    .href;
};

type TCar = {
  rotation: number;
  top: number;
  left: number;
  color: CarColors[number];
};

type CarAnimation = {
  car: TCar;
  road: 'top' | 'bottom';
  animationDelay: string;
  hidden?: boolean;
};

const carAnimations = ref<CarAnimation[]>([]);

const createNewCar = (color: CarColors[number]): TCar => {
  return {
    color,
    left: 0,
    top: 0,
    rotation: 0,
  };
};

const randomize = () => {
  return Math.random() >= 0.33;
};

const randomizeAnimation = (animation: CarAnimation) => {
  const randomIndex = Math.floor(Math.random() * carColors.length);
  const randomColor = carColors[randomIndex];

  animation.car = createNewCar(randomColor);
  animation.hidden = randomize();
};

whenever(
  isLoadingScreenTransitionendActive,
  () => {
    for (let index = 0; index < MAX_NUMBER_OF_CARS_PER_ROAD; index++) {
      const randomIndex = Math.floor(Math.random() * carColors.length);
      const randomColor = carColors[randomIndex];
      carAnimations.value.push({
        car: createNewCar(randomColor),
        road: 'top',
        animationDelay: `${index * (DURATION / MAX_NUMBER_OF_CARS_PER_ROAD)}s`,
        hidden: randomize(),
      });
      carAnimations.value.push({
        car: createNewCar(randomColor),
        road: 'bottom',
        animationDelay: `${index * (DURATION / MAX_NUMBER_OF_CARS_PER_ROAD)}s`,
        hidden: randomize(),
      });
    }
  },
  {
    immediate: true,
    once: true,
  },
);

const { isAnimationPlaying } = useApp();
</script>

<template>
  <template v-for="animation in carAnimations" :key="animation.left">
    <div
      @animationiteration="() => randomizeAnimation(animation)"
      style="background-size: 2255px 42px"
      :style="{
        'background-image': `url(${isCarHighQuality ? getCarSprite100(animation.car.color) : getCarSprite50(animation.car.color)})`,
        'animation-delay': `${animation.animationDelay}`,
        'animation-play-state': `${isAnimationPlaying ? 'running' : 'paused'}`,
      }"
      class="absolute z-rgb-animations h-[42px] w-[70px] max-w-[initial] -translate-x-[60%] -translate-y-[60%] transform-gpu bg-contain bg-no-repeat"
      :class="[
        animation.road === 'bottom' ? 'custom-car-animation-bottom' : 'custom-car-animation-top',
        animation.hidden && 'invisible',
      ]"
    />
  </template>
</template>

<style scoped>
.custom-car-animation-bottom {
  --sprite-offset: -71px;
  animation:
    roadr-bottom 20s linear infinite,
    rotatr-bottom 20s steps(1) infinite;
}

.custom-car-animation-top {
  --sprite-offset: -71px;
  animation:
    roadr-top 20s linear infinite,
    rotatr-top 20s steps(1) infinite;
}

@keyframes rotatr-bottom {
  0% {
    background-position-x: calc(var(--sprite-offset) * 8);
  }

  23.5% {
    background-position-x: calc(var(--sprite-offset) * 7);
  }

  24.5% {
    background-position-x: calc(var(--sprite-offset) * 6);
  }

  25.75% {
    background-position-x: calc(var(--sprite-offset) * 5);
  }

  26.875% {
    background-position-x: calc(var(--sprite-offset) * 4);
  }

  28% {
    background-position-x: calc(var(--sprite-offset) * 3);
  }

  29.125% {
    background-position-x: calc(var(--sprite-offset) * 2);
  }

  30.125% {
    background-position-x: calc(var(--sprite-offset) * 1);
  }

  31.375% {
    background-position-x: calc(var(--sprite-offset) * 0);
  }

  76.75% {
    background-position-x: calc(var(--sprite-offset) * 1);
  }

  78.75% {
    background-position-x: calc(var(--sprite-offset) * 2);
  }

  80.125% {
    background-position-x: calc(var(--sprite-offset) * 3);
  }

  81.5% {
    background-position-x: calc(var(--sprite-offset) * 4);
  }

  83% {
    background-position-x: calc(var(--sprite-offset) * 5);
  }

  84.75% {
    background-position-x: calc(var(--sprite-offset) * 6);
  }

  85.625% {
    background-position-x: calc(var(--sprite-offset) * 7);
  }

  86.75% {
    background-position-x: calc(var(--sprite-offset) * 8);
  }

  100% {
    background-position-x: calc(var(--sprite-offset) * 8);
  }
}

@keyframes rotatr-top {
  0% {
    background-position-x: calc(var(--sprite-offset) * 24);
  }

  14.5% {
    background-position-x: calc(var(--sprite-offset) * 23);
  }

  15.375% {
    background-position-x: calc(var(--sprite-offset) * 22);
  }

  17% {
    background-position-x: calc(var(--sprite-offset) * 21);
  }

  18% {
    background-position-x: calc(var(--sprite-offset) * 20);
  }

  18.375% {
    background-position-x: calc(var(--sprite-offset) * 19);
  }

  18.75% {
    background-position-x: calc(var(--sprite-offset) * 19);
  }

  19.375% {
    background-position-x: calc(var(--sprite-offset) * 18);
  }

  20.125% {
    background-position-x: calc(var(--sprite-offset) * 17);
  }

  21.25% {
    background-position-x: calc(var(--sprite-offset) * 16);
  }

  69% {
    background-position-x: calc(var(--sprite-offset) * 17);
  }

  70.25% {
    background-position-x: calc(var(--sprite-offset) * 18);
  }

  71.375% {
    background-position-x: calc(var(--sprite-offset) * 19);
  }

  72.375% {
    background-position-x: calc(var(--sprite-offset) * 20);
  }

  73% {
    background-position-x: calc(var(--sprite-offset) * 21);
  }

  74.625% {
    background-position-x: calc(var(--sprite-offset) * 22);
  }

  75.625% {
    background-position-x: calc(var(--sprite-offset) * 23);
  }

  76.75% {
    background-position-x: calc(var(--sprite-offset) * 24);
  }

  100% {
    background-position-x: calc(var(--sprite-offset) * 24);
  }
}

@keyframes roadr-bottom {
  0% {
    top: 1451.1545715332px;
    left: 20.61572265625px;
  }

  23.5% {
    top: 1012.60990715027px;
    left: 1070.7724609375px;
  }

  24.5% {
    top: 998.038288116455px;
    left: 1117.19372558594px;
  }

  25.75% {
    top: 986.569625377655px;
    left: 1176.92932128906px;
  }

  26.875% {
    top: 981.790512561798px;
    left: 1231.47192382813px;
  }

  28% {
    top: 982.324567556381px;
    left: 1286.21899414063px;
  }

  29.125% {
    top: 988.494129180908px;
    left: 1340.61633300781px;
  }

  30.125% {
    top: 999.016946792603px;
    left: 1388.13012695313px;
  }

  31.375% {
    top: 1019.12642288208px;
    left: 1445.52172851563px;
  }

  76.75% {
    top: 1960.82781982422px;
    left: 3443.8115234375px;
  }

  78.75% {
    top: 1991.89349365234px;
    left: 3536.013671875px;
  }

  80.125% {
    top: 2004.86889648437px;
    left: 3601.65258789063px;
  }

  81.5% {
    top: 2010.71997070312px;
    left: 3668.31030273437px;
  }

  83% {
    top: 2009.7861328125px;
    left: 3741.30517578125px;
  }

  84.75% {
    top: 2000.53759765625px;
    left: 3825.97094726562px;
  }

  85.625% {
    top: 1991.103515625px;
    left: 3867.4873046875px;
  }

  86.75% {
    top: 1972.69665527344px;
    left: 3919.04150390625px;
  }

  100% {
    top: 1725.75px;
    left: 4515px;
  }
}

@keyframes roadr-top {
  0% {
    top: 1688.35125732422px;
    left: 4504.3857421875px;
  }

  14.5% {
    top: 1956.74755859375px;
    left: 3857.9541015625px;
  }

  15.375% {
    top: 1967.25665283203px;
    left: 3816.68286132812px;
  }

  17% {
    top: 1979.78063964844px;
    left: 3738.57299804687px;
  }

  18% {
    top: 1983.21154785156px;
    left: 3690.01416015625px;
  }

  18.375% {
    top: 1983.28039550781px;
    left: 3671.7548828125px;
  }

  18.75% {
    top: 1982.57495117188px;
    left: 3653.50927734375px;
  }

  19.375% {
    top: 1979.58728027344px;
    left: 3623.22875976563px;
  }

  20.125% {
    top: 1973.02349853516px;
    left: 3587.31372070313px;
  }

  21.25% {
    top: 1957.98211669922px;
    left: 3534.66235351563px;
  }

  69% {
    top: 977.600286483765px;
    left: 1426.6904296875px;
  }

  70.25% {
    top: 962.54860496521px;
    left: 1367.74267578125px;
  }

  71.375% {
    top: 954.300466537476px;
    left: 1313.60302734375px;
  }

  73% {
    top: 950.630664885044px;
    left: 1234.60559082031px;
  }

  75.625% {
    top: 964.878098487854px;
    left: 1107.7939453125px;
  }

  76.75% {
    top: 979.433290481567px;
    left: 1055.00866699219px;
  }

  100% {
    top: 1415px;
    left: 0px;
  }
}
</style>
